import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { commonConstants, retrieveMailingAddress } from '../../CONSTANTS/tpi-common-constants';
import { tpiErrorTags } from '../../CONSTANTS/tpi-error-tags';
import { tpiSearchError } from '../../CONSTANTS/tpi-search-errors';
import { EcifService } from '../../service/ecif.service';
import { AutoAddressSearchRequest } from '../../tpi-models/general/auto-sddress-search-request';
import { AddressVinValidationResponse, PolicyVinResponse } from '../../tpi-models/general/auto-validation-request-responses';
import { CustomerAgreement } from '../../tpi-models/general/ecifResponse';
import { CoveragesResponse } from '../../tpi-models/general/coverages-models';
import { PolicyEmitters } from '../../tpi-models/general/policy-information';
import { SearchFields } from '../../tpi-models/general/search-forms';
import { LoggerService } from '../logger/logger.service';
import { PolicySearchStateServiceService } from '../tpi-statemanagement/policy-search-state-service.service';
import { PolicySearchAdapterService } from './policy-search-adapter.service';
import { WINDOW_TOKEN } from '../../tokens/injection-tokens';
import { Drivers, PolicyConsumerData, PolicyInformationDetails, PolicyRequestsCommonService, PolicyRequestsData, Vehicles } from '@nationwide/dgs-internet-servicing-policy-requests';
import { DistributionPartnerAPI } from '@nationwide/internet-servicing-dpim-requests';
import { PolicyHolders } from '../../tpi-models/policy-requests';
import { SearchResultsAdapterService } from '../../tpi-components/policy-inquiry/shared-components/search-results/search-results-adapter.service';
import { DistributionPartnerIndividualInfoService } from '../../shared/services/distribution-partner-individual-info/distribution-partner-individual-info.service';
import { PolicySearchResultsService } from './policy-search-results.service';
import { AutoDriverData, AutoPolicyData, AutoSearchResult, AutoVehicleData, buildAutoSearchResult, buildPropertySearchResult, PropertySearchResult } from '../../tpi-models/general/policy-search-results';
import { AutoDriver } from '../../tpi-models/auto-driver';

/** Internal shape of the vehicles + coverages pipeline result within {@link PolicySearchService.getAutoPolicy}. */
interface VehiclesAndCoveragesIntermediate {
    vehicleInformationResponse: Vehicles[] | HttpErrorResponse;
    coverageInformationResponse: CoveragesResponse | HttpErrorResponse | null;
}

@Injectable({
    providedIn: 'root'
})
export class PolicySearchService {
    disablePrint: boolean;
    fullVin: string;
    private distributionPartnerIndividualInfoService = inject(DistributionPartnerIndividualInfoService);
    private ecif = inject(EcifService);
    private loggerService = inject(LoggerService);
    private policyRequestsCommonService = inject(PolicyRequestsCommonService);
    private policySearchAdapter = inject(PolicySearchAdapterService);
    private policySearchResultService = inject(PolicySearchResultsService);
    private searchResultsAdapter = inject(SearchResultsAdapterService);
    private storeStateSearchFields = inject(PolicySearchStateServiceService);
    private window = inject(WINDOW_TOKEN);

    getAutoPolicy(searchFields: SearchFields): Observable<AutoSearchResult> {
        const policyNumber = searchFields.action.policyNumber;
        const vin = searchFields.action.vin;
        const euaToken = sessionStorage.getItem('jwt');
        const policyRequestsData: PolicyRequestsData = {
            consumerData: {
                accessToken: euaToken,
                camSessionId: '',
                policyNumber
            },
            productType: commonConstants.AUTO.PRODUCT_TYPE,
            policyId: policyNumber
        };

        const policy$ = this.policyRequestsCommonService.getPolicy(policyRequestsData).pipe(
            catchError((error) => {
                this.loggerService.error('Error In  Retrieve Policy Information API - retrievePolicyInformation()', { policyNumber, error });
                return of(error);
            })
        );

        const drivers$ = this.policyRequestsCommonService.getPolicyDrivers(policyRequestsData).pipe(
            catchError((error) => {
                this.loggerService.error('Error In Retrieve Driver Information API - retrieveAllDrivers() ', { policyNumber, error });
                return of(error);
            })
        );

        // Coverages depend on vehicles, so switchMap is required here to chain the two requests
        const vehiclesAndCoverages$ = this.policyRequestsCommonService.getPolicyVehicles(policyRequestsData).pipe(
            switchMap((vehicleInformationResponse: Vehicles[]) => {
                if (vehicleInformationResponse instanceof HttpErrorResponse) {
                    return of({ vehicleInformationResponse, coverageInformationResponse: null });
                }
                return this.policyRequestsCommonService.getPolicyCoverages(policyRequestsData).pipe(
                    map((coverageInformationResponse: CoveragesResponse) => ({ vehicleInformationResponse, coverageInformationResponse })),
                    catchError((error) => {
                        this.loggerService.error('Error In Retrieve Coverage Information API - retrieveCoverages()', { policyNumber, error });
                        return of({ vehicleInformationResponse, coverageInformationResponse: error });
                    })
                );
            }),
            catchError((error) => {
                this.loggerService.error('Error In Retrieve Vehicle Information API - retrieveAllVehicles()', { policyNumber, error });
                return of({ vehicleInformationResponse: error, coverageInformationResponse: null });
            })
        );

        const agent$ = this.distributionPartnerIndividualInfoService.fetchDistributionPartnerIndividualInfo(policyNumber, euaToken).pipe(
            catchError((error) => {
                this.loggerService.error('Error In Retrieve agent Information API - fetchDistributionPartnerIndividualInfo()', { policyNumber, error });
                return of(error);
            })
        );

        return forkJoin([policy$, drivers$, vehiclesAndCoverages$, agent$]).pipe(
            map(([policyInformationResponse, driverInformationResponse, vehiclesAndCoveragesResponse, agentResponse]: [
                PolicyInformationDetails | HttpErrorResponse,
                Drivers[] | HttpErrorResponse,
                VehiclesAndCoveragesIntermediate,
                DistributionPartnerAPI.DpimRoleInfoResponse | HttpErrorResponse
            ]) =>
                buildAutoSearchResult({
                    policyInformationResponse: this.transformPolicyResponse(policyInformationResponse, policyNumber, vin),
                    driverInformationResponse: this.transformDriverResponse(driverInformationResponse, policyNumber, vin),
                    vehiclesAndCoveragesResponse: this.transformVehiclesAndCoveragesResponse(vehiclesAndCoveragesResponse, policyNumber, vin),
                    agentResponse: agentResponse instanceof HttpErrorResponse ? null : agentResponse,
                    policyNumber,
                    vin
                })
            )
        );
    }

    getAutoPolicyNumberAddressSearch(autoAddressSearchRequest: AutoAddressSearchRequest, fullVin: string, emitters: PolicyEmitters): void {
        this.loggerService.info('Auto address search started', { street: autoAddressSearchRequest.street, city: autoAddressSearchRequest.city, state: autoAddressSearchRequest.state, fullVin });

        const autoSearchFields = emitters.autoSearchFields;
        emitters.showResultEmit.emit(false);

        this.ecif.validateAutoAddressEcn(autoAddressSearchRequest).pipe(
            switchMap((ecifCustomerNumbers: string[]) =>
                this.validateAddressForAllCustomers(ecifCustomerNumbers, fullVin, autoSearchFields)
            ),
            map((vinMatches: boolean[]): boolean => vinMatches.includes(true))
        ).subscribe({
            next: (isValid: boolean) => {
                if (isValid) {
                    autoSearchFields.action.displayResults = true;
                    this.storeStateSearchFields.updateSearchFields(autoSearchFields);
                    emitters.autoSearchEmit.emit(autoSearchFields);
                    this.disablePrint = false;
                    emitters.disablePrint.set(this.disablePrint);

                    this.loggerService.info('TPI-PLDS-V2: Successful Search Auto [address]', {
                        UserId: emitters.session.context.session.account.shortName,
                        PolicyNumber: autoSearchFields.action.policyNumber,
                        Vin: autoSearchFields.action.vin
                    });
                } else {
                    this.disablePrint = true;
                    emitters.disablePrint.set(this.disablePrint);
                    autoSearchFields.errorMessage = tpiSearchError.ADDRESS_OR_POLICY_NOT_FOUND;
                    autoSearchFields.hasError = true;

                    this.loggerService.error('TPI-PLDS-V2: Error Search Auto [Address]', {
                        UserId: emitters.session.context.session.account.shortName,
                        PolicyNumber: autoSearchFields.policyNumber,
                        Vin: fullVin,
                        ErrorMessage: autoSearchFields.errorMessage
                    });

                    this.storeStateSearchFields.updateSearchFields(autoSearchFields);
                    emitters.autoSearchEmit.emit(autoSearchFields);
                    this.window.waitIndicatorFullscreen.hide();
                }
            },
            error: (error: HttpErrorResponse) => {
                this.disablePrint = true;
                emitters.disablePrint.set(this.disablePrint);
                autoSearchFields.errorMessage = error.error?.developerMessage ?? tpiSearchError.UNKNOWN_APPLICATION_ERROR;
                autoSearchFields.hasError = true;

                this.loggerService.error('TPI-PLDS-V2: Error Search Auto [Address]', {
                    UserId: emitters.session.context.session.account.shortName,
                    PolicyNumber: autoSearchFields.policyNumber,
                    Vin: autoSearchFields.vin,
                    ErrorMessage: autoSearchFields.errorMessage
                });

                this.storeStateSearchFields.updateSearchFields(autoSearchFields);
                emitters.autoSearchEmit.emit(autoSearchFields);
                this.window.waitIndicatorFullscreen.hide();
            }
        });
    }

    getAutoPolicyNumberPolicyVinSearch(policyNumber: string, sixDigitVin: string, emitters: PolicyEmitters): void {
        this.loggerService.info('Auto policy-vin search started', { policyNumber, sixDigitVin });

        const autoSearchFields = emitters.autoSearchFields;
        emitters.showResultEmit.emit(false);

        this.ecif.validateAutoPolicyVin(policyNumber, sixDigitVin).subscribe({
            next: (policyVin: PolicyVinResponse) => {
                this.loggerService.info(
                    'TPI-PLDS-V2: Successful Search Auto [Policy-Vin]',
                    {
                        UserId: emitters.session?.context.session.account.shortName || '',
                        PolicyNumber: policyVin.policyNumber,
                        Vin: autoSearchFields.action.vin
                    }
                );

                this.fullVin = autoSearchFields.action.vin;
                autoSearchFields.action.displayResults = true;
                emitters.autoSearchFields.action.vin = policyVin?.vin;
                autoSearchFields.action.policyNumber = policyVin?.policyNumber?.replace('%20', ' ');
                this.storeStateSearchFields.updateSearchFields(autoSearchFields);
                emitters.autoSearchEmit.emit(autoSearchFields);
                this.disablePrint = false;
                emitters.disablePrint.set(this.disablePrint);
            },
            error: (error: HttpErrorResponse) => {
                autoSearchFields.hasError = true;
                autoSearchFields.errorMessage = error.error?.developerMessage ?? tpiSearchError.UNKNOWN_APPLICATION_ERROR;
                this.disablePrint = true;
                emitters.disablePrint.set(this.disablePrint);
                this.storeStateSearchFields.updateSearchFields(autoSearchFields);
                emitters.autoSearchEmit.emit(autoSearchFields);

                this.loggerService.error(
                    'TPI-PLDS-V2: Error Search Auto [Policy-Vin]',
                    {
                        UserId: emitters.session.context.session.account.shortName,
                        PolicyNumber: autoSearchFields.policyNumber,
                        ErrorMessage: autoSearchFields.errorMessage,
                        Vin: autoSearchFields.vin
                    }
                );

                this.window.waitIndicatorFullscreen.hide();
            }
        });
    }

    getPropertyPolicy(searchFields: SearchFields): Observable<PropertySearchResult> {
        const searchValues = {
            policyNumber: searchFields.action.policyNumber,
            zipCode: searchFields.zipCode,
            productType: searchFields.propertyType,
            ecn: searchFields.ecn
        };

        const euaToken = sessionStorage.getItem('jwt');
        const consumerData: PolicyConsumerData = {
            accessToken: euaToken,
            camSessionId: '',
            policyNumber: searchFields.action.policyNumber
        };

        return this.policySearchResultService.getPropertySearchResults(searchValues, consumerData, euaToken).pipe(
            map((raw: unknown[]) => buildPropertySearchResult(raw))
        );
    }

    getPropertySearchResults(policyNumber: string, zipCode: string, emitters: PolicyEmitters): void {
        const autoSearchFields = emitters.autoSearchFields;
        emitters.showPropertySearchEmit.emit(false);

        this.loggerService.info('Property search started', { PolicyNumber: policyNumber, ZipCode: zipCode });

        this.ecif.validatePolicyWithEcifAgreement(policyNumber, zipCode, sessionStorage.getItem('jwt'))
            .pipe(
                map((customerAgreement: CustomerAgreement) => this.policySearchAdapter.getAndMapPropertyInformation(customerAgreement, zipCode)),
                catchError((error) => throwError(() => error))
            )
            .subscribe({
                next: (response) => {
                    if (response?.success) {
                        this.loggerService.info('Property search successful', { PolicyNumber: policyNumber, ZipCode: zipCode });

                        const mappingResponse = response.success;
                        autoSearchFields.action.displayResults = true;
                        autoSearchFields.action.policyNumber = mappingResponse.policyNumber;
                        autoSearchFields.zipCode = mappingResponse.zipCode;
                        autoSearchFields.propertyType = mappingResponse.productType;
                        autoSearchFields.accountNumber = mappingResponse.billingAccountNumber;
                        autoSearchFields.ecn = mappingResponse.ecn;
                        this.storeStateSearchFields.updateSearchFields(autoSearchFields);
                        emitters.propertySearchEmit.emit(autoSearchFields);
                        this.disablePrint = false;
                        emitters.disablePrint.set(this.disablePrint);
                    } else if (response?.error) {
                        this.loggerService.error('Property search failed', { PolicyNumber: policyNumber, ZipCode: zipCode, ErrorMessage: response.error });

                        autoSearchFields.hasError = true;
                        autoSearchFields.errorMessage = response.error;
                        this.disablePrint = true;
                        emitters.disablePrint.set(this.disablePrint);
                        this.storeStateSearchFields.updateSearchFields(autoSearchFields);
                        emitters.propertySearchEmit.emit(autoSearchFields);
                        this.window.waitIndicatorFullscreen.hide();
                    }
                },
                error: (error) => {
                    this.loggerService.error('Property search error', { policyNumber, zipCode, status: error.status });

                    autoSearchFields.hasError = true;
                    if (error.status.toString() === commonConstants.ERROR_CONSTANTS.ERROR_403) {
                        autoSearchFields.errorMessage = this.policySearchAdapter.mapErrorResponse(tpiErrorTags.POLICY_INELIGIBLE).error;
                    } else if (error.status.toString() === commonConstants.ERROR_CONSTANTS.ERROR_404) {
                        autoSearchFields.errorMessage = this.policySearchAdapter.mapErrorResponse(tpiErrorTags.NULL_RESPONSE).error;
                    } else if (error.status.toString() === commonConstants.ERROR_CONSTANTS.ERROR_406) {
                        autoSearchFields.errorMessage = this.policySearchAdapter.mapErrorResponse(tpiErrorTags.POLICY_NOT_PROPERTY).error;
                    } else if (error.status.toString() === commonConstants.ERROR_CONSTANTS.ERROR_461) {
                        autoSearchFields.errorMessage = this.policySearchAdapter.mapErrorResponse(tpiErrorTags.POLICY_INFO_NOT_AVAILABLE_ONLINE).error;
                    } else {
                        autoSearchFields.errorMessage = this.policySearchAdapter.mapErrorResponse(tpiErrorTags.UNKNOWN_ERROR).error;
                    }

                    this.disablePrint = true;
                    emitters.disablePrint.set(this.disablePrint);
                    this.storeStateSearchFields.updateSearchFields(autoSearchFields);
                    emitters.propertySearchEmit.emit(autoSearchFields);
                    this.window.waitIndicatorFullscreen.hide();
                },
                complete: () => {
                    this.loggerService.info('Property search completed');
                }
            });
    }

    private transformDriverResponse(driverInformationResponse: Drivers[] | HttpErrorResponse, policyNumber: string, vin: string): AutoDriverData {
        const autoDriver: AutoDriver = { excludedNonDriver: [], driversFullName: [] };
        if (driverInformationResponse instanceof HttpErrorResponse) {
            return { drivers: [], autoDriver };
        }
        this.loggerService.info('Driver API response - retrieveAllDrivers()', { Policy: policyNumber, Vin: vin, driverInformation: driverInformationResponse });
        const drivers: Drivers[] = driverInformationResponse.filter((driver) =>
            ['Driver', 'PrincipalDriver', 'Excluded', 'NonDriver', 'OccasionalDriver'].includes(driver.driverType.toString())
        );
        for (const driver of drivers) {
            const middleName = driver.person.middleName?.length > 0 ? ` ${driver.person.middleName.slice(0, 1)}` : '';
            const fullName = `${driver.person.firstName} ${middleName} ${driver.person.lastName} ${driver.person.suffix || ''}`;
            if (driver.driverType === 'Excluded' || driver.driverType === 'NonDriver') {
                autoDriver.excludedNonDriver.push(fullName);
            } else {
                autoDriver.driversFullName.push(fullName);
            }
        }
        return { drivers, autoDriver };
    }

    private transformPolicyResponse(policyInformationResponse: PolicyInformationDetails | HttpErrorResponse, policyNumber: string, vin: string): AutoPolicyData {
        if (policyInformationResponse instanceof HttpErrorResponse) {
            return { policyNumber };
        }
        this.loggerService.info('Policy Information API response - retrievePolicyInformation()', { Policy: policyNumber, Vin: vin, policyInformation: policyInformationResponse });
        const policyInformationDetails: PolicyInformationDetails = policyInformationResponse;
        const hasCancellation = policyInformationDetails.cancellationDetails != null;
        const mailingAddress = retrieveMailingAddress[policyInformationDetails.policyAddress.state];
        this.loggerService.info('Mailing address Details', { policyNumber, vin, mailingAddress });
        const person = policyInformationDetails.policyHolders.filter(
            (policyHolderType: PolicyHolders) => policyHolderType.policyHolderType === 'PolicyPriNamedInsured'
        )[0]?.person;
        return {
            policyNumber,
            policyInformationDetails,
            policyInformation: { policyStatus: hasCancellation ? 'Cancelled' : 'Active', policyType: 'Auto' },
            cancellationDetailsForTPI: hasCancellation ? policyInformationDetails.cancellationDetails : undefined,
            mailingAddress,
            person
        };
    }

    private transformVehiclesAndCoveragesResponse(vehiclesAndCoveragesResponse: VehiclesAndCoveragesIntermediate, policyNumber: string, vin: string): AutoVehicleData {
        const { vehicleInformationResponse, coverageInformationResponse } = vehiclesAndCoveragesResponse;
        if (vehicleInformationResponse instanceof HttpErrorResponse) {
            return { vehicles: [], coverages: [] };
        }
        this.loggerService.info('Vehicle API response - retrieveAllVehicles()', { Policy: policyNumber, Vin: vin, vehicleInformation: vehicleInformationResponse });
        const vehicles: Vehicles[] = vehicleInformationResponse.filter((vehicle) => vehicle.vin === vin);
        if (!coverageInformationResponse || coverageInformationResponse instanceof HttpErrorResponse) {
            return { vehicles, coverages: [] };
        }
        this.loggerService.info('Coverage API response - retrieveCoverages()', { Policy: policyNumber, Vin: vin, coverageInformation: coverageInformationResponse });
        const coverages = coverageInformationResponse.coverages.filter((coverage) =>
            coverage.available === true && this.searchResultsAdapter.isPolicyLevelCoverageAndSelected(coverage, vehicles[0]?.vehicleId)
        );
        return { vehicles, coverages };
    }

    private validateAddressForAllCustomers(ecifCustomerNumbers: string[], fullVin: string, autoSearchFields: SearchFields): Observable<boolean[]> {
        const validateAddress: Observable<boolean>[] = ecifCustomerNumbers.map((customerNumber) =>
            this.validateVinsForCustomer(customerNumber, fullVin, autoSearchFields)
        );
        return forkJoin(validateAddress);
    }

    private validateVinsForCustomer(customerNumber: string, fullVin: string, autoSearchFields: SearchFields): Observable<boolean> {
        return this.ecif.validationAutoAddressEcnPolicyNumber(customerNumber).pipe(
            switchMap((policyNums: string[]) => {
                const validatePolicyNumbers: Observable<AddressVinValidationResponse>[] = policyNums.map((policyNumber) =>
                    this.ecif.validateAutoAddressVin(policyNumber, fullVin)
                );
                return forkJoin(validatePolicyNumbers);
            }),
            map((validateVins: AddressVinValidationResponse[]) => {
                const validPolicy = validateVins.find((response) => {
                    if (response.validVin) {
                        autoSearchFields.action.policyNumber = response.policyNumber?.replace('%20', ' ');
                        autoSearchFields.action.vin = fullVin;
                        return true;
                    }
                    return false;
                });
                return !!validPolicy;
            }),
            catchError((error) => {
                this.loggerService.error('Auto policy number with address not found for ECN', { error: error.error });
                const systemErrorCode = 500;
                if (error.status !== systemErrorCode) {
                    return of(false);
                }
                return throwError(() => error);
            })
        );
    }
}